import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ReviewData = any;

function Stars({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, idx) => {
        const v = idx + 1;
        const active = v <= value;
        return (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v)}
            className="p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
            aria-label={`Rate ${v} star${v === 1 ? "" : "s"}`}
          >
            <Star
              className={`h-7 w-7 ${active ? "text-yellow-500" : "text-muted-foreground"}`}
              fill={active ? "currentColor" : "none"}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ServiceReviewPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState<ReviewData | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [birthday, setBirthday] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googlePromptOpen, setGooglePromptOpen] = useState(false);

  const business = useMemo(() => (review as any)?.business || null, [review]);
  const googleReviewUrl = useMemo(() => {
    const raw = String((business as any)?.google_review_url || "").trim();
    return /^https?:\/\//i.test(raw) ? raw : "";
  }, [business]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setError("No review token provided");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke("service-review", {
          body: { action: "get", token },
        });
        
        if (invokeError) {
          console.error("Service review fetch error:", invokeError);
          setError(invokeError.message || "Failed to load review");
          setReview(null);
          setLoading(false);
          return;
        }
        
        if (data?.ok && data?.review) {
          setReview(data.review);
          const existingRating = Number(data.review?.rating || 0);
          setRating(existingRating);
          setComment(String(data.review?.comment || ""));
          setBirthday(String((data.review as any)?.customer_birthday || ""));
          setSubmitted(Boolean(data.review?.submitted_at));
        } else if (data?.error) {
          setError(data.error);
          setReview(null);
        } else {
          console.warn("Service review response:", data);
          setError("Review link not found or unavailable");
          setReview(null);
        }
      } catch (err) {
        console.error("Service review exception:", err);
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
        setReview(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const handleSubmit = async () => {
    if (!token || rating < 1) return;
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("service-review", {
      body: { action: "submit", token, rating, comment: comment || null, birthday: birthday || null },
    });
    setSubmitting(false);
    if (!error && data?.ok) {
      setSubmitted(true);
      if (googleReviewUrl) {
        setGooglePromptOpen(true);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <p className="text-sm text-muted-foreground">
            {error || "Review link not found or unavailable."}
          </p>
          {error && (
            <p className="mt-2 text-xs text-muted-foreground">
              Please check the link or contact support if this issue persists.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          {business?.logo_url ? (
            <img
              src={business.logo_url}
              alt={business?.name || "Business"}
              className="h-14 w-14 rounded-full mx-auto object-cover border border-border"
              loading="lazy"
            />
          ) : null}
          <h1 className="text-2xl font-bold mt-3 text-foreground">{business?.name || "Service Review"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Leave a quick review for your service.
          </p>
        </div>

        <Card className="w-full">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Review details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Service</span>
                <span className="font-medium text-foreground text-right">
                  {(review as any)?.service_name || "Service"}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Client</span>
                <span className="font-medium text-foreground text-right">
                  {(review as any)?.customer_name || "Customer"}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium text-foreground text-right">
                  {(review as any)?.customer_email || "—"}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Star rating</div>
              <Stars value={rating} onChange={setRating} disabled={submitted || submitting} />
              {rating < 1 && !submitted ? (
                <div className="text-xs text-muted-foreground">Tap a star to rate.</div>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Comment</div>
              <Textarea
                placeholder="Write a short review (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitted || submitting}
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Birthday (optional)</div>
              <Input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                disabled={submitted || submitting}
              />
              <div className="text-xs text-muted-foreground">
                Kindly share your birthday, so we can send you birthday treats, offers, or reminders 🎉🎂
              </div>
            </div>

            {submitted ? (
              <div className="rounded-lg border border-border bg-green-50 dark:bg-green-950/20 p-3 text-sm">
                <div className="font-semibold text-foreground">Thanks!</div>
                <div className="text-muted-foreground mt-1">Your review has been submitted.</div>
                {googleReviewUrl ? (
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open(googleReviewUrl, "_blank", "noopener,noreferrer")}
                    >
                      Leave a Google review
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={submitting || rating < 1}
              >
                {submitting ? "Submitting..." : "Submit Review"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={googlePromptOpen} onOpenChange={setGooglePromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Also leave this review on Google?</DialogTitle>
            <DialogDescription>
              Your Buukd review is saved. If you’d like, you can also post it on Google to help this business get discovered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGooglePromptOpen(false)}>
              Not now
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (googleReviewUrl) window.open(googleReviewUrl, "_blank", "noopener,noreferrer");
                setGooglePromptOpen(false);
              }}
              disabled={!googleReviewUrl}
            >
              Leave Google review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

